import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useDevTap } from 'features/dev-tools'
import { useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { Page } from 'shared/components/Page'
import { useMobileTrpc } from 'shared/api'
import { useDebouncedValue } from 'shared/lib/hooks/useDebouncedValue'
import { useTranslate } from 'shared/lib/i18n'
import {
	hasFeedFilters,
	parseFeedFilters,
	replaceFeedFilters,
} from '../lib/state'
import { CategoryChips } from './CategoryChips'
import { FeedEmptyState } from './FeedEmptyState'
import { FeedErrorState } from './FeedErrorState'
import { LotCard } from './LotCard'
import { LotCardSkeleton } from './LotCardSkeleton'
import { SearchBar } from './SearchBar'
import { SortRow } from './SortRow'

type FeedPage = { items: unknown[]; total: number; nextCursor: number | null }
type FeedData = { pages: FeedPage[]; pageParams: unknown[] }

export function Feed() {
	const trpc = useMobileTrpc()
	const queryClient = useQueryClient()
	const devTap = useDevTap()
	const { t } = useTranslate()
	const [params, setParams] = useSearchParams()
	const filters = parseFeedFilters(params)
	const { searchInput, chip, sort } = filters
	const search = useDebouncedValue(searchInput.trim(), 300)
	const hasActiveFilters = hasFeedFilters(filters)

	const clearAllFilters = () =>
		replaceFeedFilters(setParams, {
			searchInput: '',
			chip: { kind: 'all' },
			sort: 'newest',
		})

	const { data: categories } = useQuery(trpc.categories.List.queryOptions())
	const categoryNameById = useMemo(
		() => new Map((categories ?? []).map((c) => [c._id, c.name])),
		[categories],
	)

	const {
		data,
		isPending,
		isError,
		isFetching,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
		refetch,
	} = useInfiniteQuery({
		...trpc.lots.Feed.infiniteQueryOptions(
			{
				search: search || undefined,
				categoryId: chip.kind === 'category' ? chip.id : undefined,
				bookmarked: chip.kind === 'bookmarked' || undefined,
				sort,
			},
			{
				getNextPageParam: (last) => last.nextCursor,
				initialCursor: 0,
			},
		),
		// Filter/sort/search changes keep the previous grid on screen (dimmed)
		// instead of collapsing to skeletons — no layout jump.
		placeholderData: keepPreviousData,
	})

	const lots = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data])
	const total = data?.pages[0]?.total

	// Optimistic bookmark toggle across every cached Feed variant (all filters).
	const toggleBookmark = useMutation({
		...trpc.lots.ToggleBookmark.mutationOptions<{
			previous: Array<[readonly unknown[], unknown]>
		}>(),
		onMutate: async ({ lotId }) => {
			const filter = trpc.lots.Feed.pathFilter()
			await queryClient.cancelQueries(filter)
			const previous = queryClient.getQueriesData(filter)
			queryClient.setQueriesData(filter, (old: FeedData | undefined) => {
				if (!old?.pages) return old
				return {
					...old,
					pages: old.pages.map((page) => ({
						...page,
						items: page.items.map((item) => {
							const lot = item as { _id: string; isBookmarked: boolean }
							return lot._id === lotId
								? { ...lot, isBookmarked: !lot.isBookmarked }
								: item
						}),
					})),
				}
			})
			return { previous }
		},
		onError: (_err, _vars, context) => {
			for (const [key, value] of context?.previous ?? [])
				queryClient.setQueryData(key, value)
		},
		onSettled: () => queryClient.invalidateQueries(trpc.lots.Feed.pathFilter()),
	})

	// Infinite scroll: fetch the next page when the sentinel nears the viewport.
	const sentinelRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const el = sentinelRef.current
		if (!el) return
		const io = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage)
					void fetchNextPage()
			},
			{ rootMargin: '600px' },
		)
		io.observe(el)
		return () => io.disconnect()
	}, [hasNextPage, isFetchingNextPage, fetchNextPage])

	return (
		<Page>
			<div className="px-4 pt-1.5">
				<h1
					onClick={devTap}
					className="select-none text-[30px] font-bold leading-none tracking-[0.36px] text-[var(--tgui--text_color,#fff)]"
				>
					{t('navigation.catalog')}
				</h1>
				<SearchBar
					value={searchInput}
					onChange={(value) => replaceFeedFilters(setParams, { searchInput: value })}
					hasFilters={hasActiveFilters}
					onClearFilters={clearAllFilters}
				/>
				<CategoryChips
					categories={categories ?? []}
					active={chip}
					onChange={(value) => replaceFeedFilters(setParams, { chip: value })}
				/>
				<SortRow
					total={total}
					sort={sort}
					onChange={(value) => replaceFeedFilters(setParams, { sort: value })}
				/>
			</div>

			{isError ? (
				<FeedErrorState onRetry={() => void refetch()} />
			) : (
				<div
					className={`flex flex-col gap-4 px-4 pb-6 pt-4 transition-opacity ${
						isFetching && !isFetchingNextPage && !isPending ? 'opacity-60' : ''
					}`}
				>
					{isPending &&
						Array.from({ length: 4 }, (_, i) => <LotCardSkeleton key={i} />)}

					{!isPending &&
						lots.map((item) => {
							const lot = item as Parameters<typeof LotCard>[0]['lot']
							return (
								<LotCard
									key={lot._id}
									lot={lot}
									categoryNames={lot.categoryIds
										.map((id) => categoryNameById.get(id))
										.filter((n): n is string => Boolean(n))}
									onToggleBookmark={(lotId) => toggleBookmark.mutate({ lotId })}
								/>
							)
						})}

					{!isPending && lots.length === 0 && (
						<FeedEmptyState
							filtered={hasActiveFilters}
							onReset={clearAllFilters}
						/>
					)}

					{isFetchingNextPage && (
						<>
							<LotCardSkeleton />
							<LotCardSkeleton />
						</>
					)}
					<div ref={sentinelRef} />
				</div>
			)}
		</Page>
	)
}
